/**
 * Restores the user's scroll position on collection / search pages with
 * infinite scroll after they navigate back from a PDP.
 *
 * Background:
 *   <custom-pagination data-infinite-scroll="true"> in
 *   assets/custom-pagination.js fetches more results as the user scrolls.
 *   When the user clicks a product, lands on a PDP, then taps the
 *   browser back button, the collection page is re-rendered with only
 *   the first paginated batch present. The browser tries to restore the
 *   saved scroll Y, but that position is now past the bottom of the
 *   rendered content — so the user ends up at the bottom of an
 *   incomplete page instead of next to the product they were viewing.
 *
 *   Mobile browsers tend to restore from the back/forward cache (bfcache)
 *   which preserves the entire DOM including all infinite-scrolled
 *   results, so the issue is desktop-specific.
 *
 * Approach:
 *   - On a click on any link inside .js-pagination-result on a PLP/search
 *     page, save { scrollY, count, timestamp } to sessionStorage keyed by
 *     the current path+query.
 *   - On collection/search page load, look up that key. If present and
 *     recent, programmatically click the "Load more" button (which the
 *     existing custom-pagination wires up) repeatedly until the result
 *     count meets or exceeds the saved count or no more results remain,
 *     then scroll back to the saved Y.
 *
 * Self-contained: no-ops on pages without <custom-pagination>.
 */
(() => {
  const STORAGE_PREFIX = 'plp-scroll:';
  const MAX_AGE_MS = 30 * 60 * 1000; // 30 minutes
  const MAX_PAGES_TO_REPLAY = 30;
  const WAIT_FOR_PAGE_MS = 8000;
  const POLL_MS = 80;

  function getPagination() {
    return document.querySelector('custom-pagination[data-infinite-scroll="true"]');
  }

  function storageKey() {
    return STORAGE_PREFIX + window.location.pathname + window.location.search;
  }

  // ----- Capture state on product-link click ------------------------------
  document.addEventListener(
    'click',
    (evt) => {
      const link = evt.target.closest('a');
      if (!link || !link.href) return;
      if (link.href.startsWith('javascript:')) return;

      const card = link.closest('.js-pagination-result');
      if (!card) return;

      const pagination = getPagination();
      if (!pagination) return;

      const count = pagination.querySelectorAll('.js-pagination-result').length;
      const payload = JSON.stringify({
        scrollY: window.scrollY || window.pageYOffset || 0,
        count,
        timestamp: Date.now(),
      });
      try {
        sessionStorage.setItem(storageKey(), payload);
      } catch (e) {
        // sessionStorage full or disabled — silently ignore.
      }
    },
    { capture: true }
  );

  // ----- Restore on collection/search page load ---------------------------
  function readSaved() {
    let raw;
    try {
      raw = sessionStorage.getItem(storageKey());
    } catch (e) {
      return null;
    }
    if (!raw) return null;
    try {
      sessionStorage.removeItem(storageKey());
    } catch (e) {
      /* ignore */
    }
    let saved;
    try {
      saved = JSON.parse(raw);
    } catch (e) {
      return null;
    }
    if (!saved || typeof saved.scrollY !== 'number' || typeof saved.count !== 'number') return null;
    if (Date.now() - saved.timestamp > MAX_AGE_MS) return null;
    return saved;
  }

  function waitForCountIncrease(pagination, beforeCount) {
    return new Promise((resolve) => {
      const start = Date.now();
      const tick = () => {
        const now = pagination.querySelectorAll('.js-pagination-result').length;
        if (now > beforeCount) return resolve();
        if (Date.now() - start > WAIT_FOR_PAGE_MS) return resolve();
        setTimeout(tick, POLL_MS);
      };
      tick();
    });
  }

  async function restore() {
    const pagination = getPagination();
    if (!pagination) return;

    const saved = readSaved();
    if (!saved) return;

    // Stop the browser from auto-restoring while we're rebuilding the list.
    try {
      history.scrollRestoration = 'manual';
    } catch (e) {
      /* ignore */
    }

    // Disable any smooth-scroll behaviour during placement.
    const html = document.documentElement;
    const origBehavior = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';

    let attempts = 0;
    while (
      pagination.querySelectorAll('.js-pagination-result').length < saved.count &&
      attempts < MAX_PAGES_TO_REPLAY
    ) {
      const loadBtn = pagination.querySelector('.js-pagination-load-more');
      if (!loadBtn || loadBtn.classList.contains('is-loading')) break;

      const beforeCount = pagination.querySelectorAll('.js-pagination-result').length;
      loadBtn.click();
      await waitForCountIncrease(pagination, beforeCount);
      attempts += 1;
    }

    window.scrollTo({ top: saved.scrollY, left: 0, behavior: 'auto' });
    html.style.scrollBehavior = origBehavior;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', restore);
  } else {
    restore();
  }
})();
